// YNX — MASTER ADMIN PANEL (Carl TACITA only)
// Accessible via /?admin=YOUR_OWNER_SECRET — never indexed, never linked from UI.
import React, { useEffect, useState, useCallback } from 'react';
import {
  Crown, Bell, Building2, Users, ReceiptText, CalendarDays,
  Phone, KanbanSquare, Shield, ShieldOff, Trash2, RefreshCw,
  CheckCheck, Mail, MapPin, LogOut, Lock, Activity, Stethoscope,
} from 'lucide-react';
import {
  adminWhoami, adminListWorkspaces, adminBlockWorkspace,
  adminUnblockWorkspace, adminDeleteWorkspace,
  adminListNotifications, adminUnreadCount,
  adminMarkRead, adminMarkAllRead,
} from '../lib/biz_admin';
import { VisitorsPanel } from './VisitorsPanel';
import { DiagnosticsPanel } from './DiagnosticsPanel';
import './diag.css';

// Storage key NAME (not a secret) used to remember the owner secret between
// reloads. We use sessionStorage so the secret is wiped when the tab closes.
const OWNER_STORAGE_KEY = 'ynx_owner_session';

export const MasterAdmin = ({ initialSecret = '' }) => {
  const [secret, setSecret] = useState(initialSecret || sessionStorage.getItem(OWNER_STORAGE_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [tab, setTab] = useState('workspaces'); // 'workspaces' | 'notifications'
  const [workspaces, setWorkspaces] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState(''); // '' | 'active' | 'blocked'
  const [loading, setLoading] = useState(true);

  // ─── auth on mount ──
  useEffect(() => {
    (async () => {
      if (!secret) { setBusy(false); return; }
      try { await adminWhoami(secret); setAuthed(true); setError(''); sessionStorage.setItem(OWNER_STORAGE_KEY, secret); }
      catch (e) { setError(e.message); setAuthed(false); }
      finally { setBusy(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── data refresh ──
  const refresh = useCallback(async () => {
    if (!authed || !secret) return;
    setLoading(true);
    try {
      const [wsList, ntfs, count] = await Promise.all([
        adminListWorkspaces(secret, filter),
        adminListNotifications(secret),
        adminUnreadCount(secret),
      ]);
      setWorkspaces(wsList);
      setNotifications(ntfs);
      setUnread(count.unread || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [authed, secret, filter]);

  useEffect(() => { if (authed) refresh(); }, [authed, refresh]);

  // ─── auto-poll every 15s for new notifications ──
  useEffect(() => {
    if (!authed) return undefined;
    const tid = setInterval(async () => {
      try {
        const c = await adminUnreadCount(secret);
        if (c.unread !== unread) refresh();
      } catch (err) { console.warn('YNX admin: poll failed', err); }
    }, 15000);
    return () => clearInterval(tid);
  }, [authed, secret, unread, refresh]);

  const tryAuth = async () => {
    setBusy(true); setError('');
    try {
      await adminWhoami(secret);
      sessionStorage.setItem(OWNER_STORAGE_KEY, secret);
      setAuthed(true);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const logout = () => {
    sessionStorage.removeItem(OWNER_STORAGE_KEY);
    setAuthed(false); setSecret('');
  };

  const block = async (ws) => {
    if (!window.confirm(`Bloquer définitivement "${ws.name}" ? Les utilisateurs ne pourront plus s'y connecter.`)) return;
    await adminBlockWorkspace(secret, ws.id);
    refresh();
  };
  const unblock = async (ws) => {
    await adminUnblockWorkspace(secret, ws.id);
    refresh();
  };
  const removeWs = async (ws) => {
    if (!window.confirm(`SUPPRESSION DÉFINITIVE de "${ws.name}" et de TOUTES ses données (factures, clients, RDV…). Cette action est IRRÉVERSIBLE. Confirmer ?`)) return;
    await adminDeleteWorkspace(secret, ws.id);
    refresh();
  };
  const markAllRead = async () => {
    await adminMarkAllRead(secret);
    refresh();
  };
  const openWs = (ws) => {
    setTab('workspaces');
    // Highlight via scroll
    setTimeout(() => {
      const el = document.querySelector(`[data-ws-id="${ws.workspace_id || ws.id}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // ─── LOGIN SCREEN ───
  if (!authed) {
    return (
      <div className='biz-onboard-wrap'>
        <div className='biz-onboard-card' style={{ maxWidth: 460 }}>
          <Crown size={42} style={{ color: '#fbbf24' }} />
          <h1 className='biz-onboard-title' style={{ marginTop: 12 }}>Panneau Master Admin</h1>
          <p className='biz-onboard-sub'>Réservé à <b>Carl TACITA</b> · YNX</p>
          <label className='biz-label'>Secret administrateur</label>
          <input
            className='biz-input'
            type='password'
            name='owner-secret'
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && tryAuth()}
            autoFocus
            placeholder='YNX_OWNER_SECRET'
            data-testid='admin-secret-input'
          />
          <button className='biz-btn-primary' onClick={tryAuth} disabled={busy || !secret} data-testid='admin-login-btn'>
            <Lock size={14} /> {busy ? 'Vérification…' : 'Accéder au panneau'}
          </button>
          {error && <div className='biz-error'>⚠ {error}</div>}
          <div className='biz-onboard-footer'>
            Si vous arrivez ici par erreur, fermez cet onglet.
          </div>
        </div>
      </div>
    );
  }

  // ─── ADMIN PANEL ───
  return (
    <div className='admin-wrap'>
      <header className='admin-header'>
        <div className='admin-header-brand'>
          <Crown size={22} style={{ color: '#fbbf24' }} />
          <div>
            <div className='admin-title'>YNX MASTER ADMIN</div>
            <div className='admin-sub'>Carl TACITA · Tableau de bord propriétaire</div>
          </div>
        </div>
        <div className='admin-header-right'>
          <button className='biz-btn-ghost sm' onClick={refresh} disabled={loading}>
            <RefreshCw size={12} /> {loading ? 'Sync…' : 'Actualiser'}
          </button>
          <button className='biz-btn-ghost sm' onClick={logout}>
            <LogOut size={12} /> Déconnexion
          </button>
        </div>
      </header>

      <nav className='admin-tabs'>
        <button
          className={`admin-tab ${tab === 'workspaces' ? 'active' : ''}`}
          onClick={() => setTab('workspaces')}
          data-testid='admin-tab-workspaces'
        >
          <Building2 size={14} /> Entreprises ({workspaces.length})
        </button>
        <button
          className={`admin-tab ${tab === 'notifications' ? 'active' : ''}`}
          onClick={() => setTab('notifications')}
          data-testid='admin-tab-notifications'
        >
          <Bell size={14} /> Notifications
          {unread > 0 && <span className='admin-badge'>{unread}</span>}
        </button>
        <button
          className={`admin-tab ${tab === 'visitors' ? 'active' : ''}`}
          onClick={() => setTab('visitors')}
          data-testid='admin-tab-visitors'
        >
          <Activity size={14} /> Visiteurs
        </button>
        <button
          className={`admin-tab ${tab === 'diagnostics' ? 'active' : ''}`}
          onClick={() => setTab('diagnostics')}
          data-testid='admin-tab-diagnostics'
        >
          <Stethoscope size={14} /> Diagnostics IA
        </button>
      </nav>

      {tab === 'visitors' && <VisitorsPanel secret={secret} />}
      {tab === 'diagnostics' && <DiagnosticsPanel secret={secret} />}

      {tab === 'workspaces' && (
        <section className='admin-section'>
          <div className='admin-filters'>
            <button className={`admin-pill ${filter === '' ? 'active' : ''}`} onClick={() => setFilter('')}>Toutes</button>
            <button className={`admin-pill ${filter === 'active' ? 'active' : ''}`} onClick={() => setFilter('active')}>Actives</button>
            <button className={`admin-pill ${filter === 'blocked' ? 'active' : ''}`} onClick={() => setFilter('blocked')}>Bloquées</button>
          </div>

          {workspaces.length === 0 && <div className='biz-empty'>Aucune entreprise.</div>}

          <div className='admin-grid'>
            {workspaces.map((ws) => {
              const isBlocked = (ws.status || 'active') === 'blocked';
              return (
                <article
                  key={ws.id}
                  data-ws-id={ws.id}
                  className={`admin-ws-card ${isBlocked ? 'blocked' : ''}`}
                >
                  <header className='admin-ws-head'>
                    <div>
                      <h3>{ws.name}</h3>
                      <code className='admin-ws-code'>{ws.access_code}</code>
                    </div>
                    <span className={`admin-status ${isBlocked ? 'blocked' : 'active'}`}>
                      {isBlocked ? '⛔ BLOQUÉE' : '● ACTIVE'}
                    </span>
                  </header>

                  <div className='admin-ws-meta'>
                    {ws.owner_email && <p><Mail size={11} /> <a href={`mailto:${ws.owner_email}`}>{ws.owner_email}</a></p>}
                    {ws.siret && <p>🏛 SIRET : {ws.siret}</p>}
                    {ws.phone && <p>📞 {ws.phone}</p>}
                    {ws.address && <p><MapPin size={11} /> {ws.address}</p>}
                    <p style={{ opacity: 0.6, fontSize: 11 }}>Inscrite le {new Date(ws.created_at).toLocaleString('fr-FR')}</p>
                  </div>

                  <div className='admin-ws-stats'>
                    <span title='Clients'><Users size={11} /> {ws._stats?.clients || 0}</span>
                    <span title='Factures'><ReceiptText size={11} /> {ws._stats?.invoices || 0}</span>
                    <span title='Devis'>📄 {ws._stats?.quotes || 0}</span>
                    <span title='Rendez-vous'><CalendarDays size={11} /> {ws._stats?.appointments || 0}</span>
                    <span title='Tâches'><KanbanSquare size={11} /> {ws._stats?.tasks || 0}</span>
                    <span title='Contacts internes'><Phone size={11} /> {ws._stats?.contacts || 0}</span>
                  </div>

                  <footer className='admin-ws-actions'>
                    {isBlocked ? (
                      <button className='biz-btn-ghost sm' onClick={() => unblock(ws)}>
                        <Shield size={12} /> Débloquer
                      </button>
                    ) : (
                      <button className='biz-btn-ghost sm danger' onClick={() => block(ws)}>
                        <ShieldOff size={12} /> Bloquer
                      </button>
                    )}
                    <button className='biz-btn-ghost sm danger' onClick={() => removeWs(ws)}>
                      <Trash2 size={12} /> Supprimer
                    </button>
                  </footer>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {tab === 'notifications' && (
        <section className='admin-section'>
          <div className='admin-filters'>
            <button className='biz-btn-ghost sm' onClick={markAllRead} disabled={unread === 0}>
              <CheckCheck size={12} /> Tout marquer comme lu ({unread})
            </button>
          </div>

          {notifications.length === 0 && <div className='biz-empty'>Aucune notification.</div>}

          <div className='admin-notif-list'>
            {notifications.map((n) => (
              <article key={n.id} className={`admin-notif-item ${n.read ? '' : 'unread'}`}>
                <div className='admin-notif-icon'><Bell size={14} /></div>
                <div className='admin-notif-body'>
                  <h4>{n.message || n.type}</h4>
                  <div className='admin-notif-meta'>
                    {n.workspace_name && <span><b>{n.workspace_name}</b></span>}
                    {n.owner_email && <span>· {n.owner_email}</span>}
                    {n.siret && <span>· SIRET {n.siret}</span>}
                    <span>· {new Date(n.created_at).toLocaleString('fr-FR')}</span>
                  </div>
                </div>
                <div className='admin-notif-actions'>
                  {!n.read && (
                    <button
                      className='biz-btn-ghost sm'
                      onClick={async () => { await adminMarkRead(secret, n.id); refresh(); }}
                    >
                      Marquer lu
                    </button>
                  )}
                  <button className='biz-btn-ghost sm' onClick={() => openWs(n)}>Voir →</button>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};
